import React, { forwardRef, HTMLAttributes, useMemo } from 'react'

import AlertDiamond from '@helpscout/hsds-icons/alert-diamond'
import type { IconType } from '@helpscout/hsds-icons/baseIcon'
import classNames from 'classnames'

import VisuallyHidden from 'hsds/components/visually-hidden'
import { STATES, TEXT_SHADES } from 'hsds/utils/constants'
import { getValidProps } from 'hsds/utils/react'
import { dasherize } from 'hsds/utils/strings'

import { IconUI } from './Icon.styles'

export const SIZES = [
  8, 10, 12, 13, 14, 15, 16, 18, 20, 24, 32, 48, 52, 60, 72,
] as const

export type IconSizeType = (typeof SIZES)[number]
export type IconStateType = (typeof STATES)[keyof typeof STATES]
export type IconShadeType = (typeof TEXT_SHADES)[number]

export type IconProps = {
  /** Center aligns component. */
  center?: boolean
  /** Custom class names to be added to the component. */
  className?: string
  /** Enables the component to be clickable. */
  clickable?: boolean
  /** Ignores click events. Bubbles click event to parent component. */
  ignoreClick?: boolean
  /** Displays the component as `inline-block`. */
  inline?: boolean
  /** The icon to render. */
  icon?: IconType
  /** Callback function when component is clicked. */
  onClick?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  /** Changes icon color to represent a state. */
  state?: IconStateType
  /** Changes icon color shade. */
  shade?: IconShadeType
  /** Adjusts the size of the component. */
  size?: IconSizeType | `${IconSizeType}`
  /** Provides a name for the component. */
  title?: string
  /** Data attr for Cypress tests. */
  'data-cy'?: string

  isWithHiddenTitle?: boolean
  /**
   * Render the `title` as a native `title` attribute, which browsers show as a
   * hover tooltip. Pass `false` when the icon is decorative and its meaning is
   * already carried by an interactive ancestor's accessible name — otherwise
   * the browser tooltip just repeats that name, or worse, shows the internal
   * icon name from the fallback. `data-icon-name` keeps the value either way.
   */
  isWithNativeTitle?: boolean
  offsetLeft?: boolean
  offsetRight?: boolean
  /** Deprecated prop */
  faint?: boolean
  /** Deprecated prop */
  muted?: boolean
  /** Deprecated prop */
  subtle?: boolean
} & HTMLAttributes<HTMLSpanElement>

function noop() {}

const Icon = forwardRef<HTMLSpanElement, IconProps>(function Icon(props, ref) {
  const {
    'data-cy': dataCy = 'Icon',
    center = false,
    className,
    clickable = false,
    ignoreClick = true,
    isWithHiddenTitle = true,
    isWithNativeTitle = true,
    inline,
    onClick = noop,
    offsetLeft = false,
    offsetRight = false,
    icon: IconComponent = AlertDiamond,
    shade,
    size = '20',
    state,
    title,
    // These 3 are deprecated, here for backwards compat only
    faint,
    muted,
    subtle,
    ...rest
  } = props

  const iconName: string = useMemo(() => {
    let name = ''

    if (IconComponent?.displayName) {
      name = IconComponent.displayName
    } else if (IconComponent?.name) {
      name = IconComponent.name
    }

    return dasherize(name)
  }, [IconComponent])

  const iconTitle = title || iconName

  // Shades backwards compat
  let iconShade = shade
  if (!iconShade) {
    if (subtle) iconShade = 'subtle'
    else if (faint) iconShade = 'faint'
    else if (muted) iconShade = 'muted'
  }

  return (
    <IconUI
      aria-hidden
      role="img"
      data-cy={dataCy}
      {...getValidProps(rest)}
      className={classNames(
        'c-Icon',
        center && 'is-center',
        clickable && 'is-clickable',
        !clickable && ignoreClick && 'is-noInteract',
        inline && 'is-inline',
        iconName && `is-iconName-${iconName}`,
        offsetLeft && 'is-offsetLeft',
        offsetRight && 'is-offsetRight',
        iconShade && `is-${iconShade}`,
        state && `is-${state}`,
        size && `is-${size}`,
        className
      )}
      onClick={onClick}
      data-icon-name={iconTitle}
      title={isWithNativeTitle ? iconTitle : undefined}
      ref={ref}
    >
      <span className="c-Icon__icon">{IconComponent && <IconComponent />}</span>
      {isWithHiddenTitle ? <VisuallyHidden>{iconTitle}</VisuallyHidden> : null}
    </IconUI>
  )
})

export default Icon
